import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import Table from '../common/Table';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';

const PGPaymentsTab = ({ pgId, onShowAddPayment }) => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState('');
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);

  useEffect(() => {
    if (pgId) {
      fetchPayments();
    }
  }, [pgId]);

  const fetchPayments = async () => {
    if (paymentsLoaded) return;
    
    try {
      setPaymentsLoading(true);
      setPaymentsError('');
      
      const response = await apiGet(`/payments?pg_id=${pgId}&limit=1000`);
      const paymentsData = response?.data || [];
      
      setPayments(paymentsData);
      
      // Calculate summary - exclude cancelled payments
      const totalPayments = paymentsData.length;
      const completedPayments = paymentsData.filter(p => p.status?.toLowerCase() !== 'cancelled');
      const totalAmountPaid = completedPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      const cancelledCount = paymentsData.filter(p => p.status?.toLowerCase() === 'cancelled').length;
      
      setPaymentSummary({
        totalPayments,
        totalAmountPaid,
        completedPayments: completedPayments.length,
        cancelledCount
      });
      
      setPaymentsLoaded(true);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPaymentsError('Failed to load payment information');
      toastError('Failed to load payments');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleAddPayment = async () => {
    try {
      const pgResponse = await apiGet(`/pgdetails/${pgId}`);
      if (pgResponse?.data) {
        onShowAddPayment(null, pgResponse.data);
      } else {
        toastError('Failed to load paying guest details');
      }
    } catch (error) {
      console.error('Error loading PG data:', error);
      toastError('Failed to load paying guest data');
    }
  };

  const paymentColumns = [
    {
      Header: 'Payment ID',
      accessor: 'payment_id'
    },
    {
      Header: 'Date',
      accessor: 'payment_date'
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: ({ row }) => `₹${row.amount?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Method',
      accessor: 'payment_method'
    },
    {
      Header: 'Type',
      accessor: 'payment_type'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => {
        const status = row.status?.toLowerCase();
        const statusColors = {
          'completed': '#28a745',
          'cancelled': '#dc3545',
          'pending': '#ffc107',
          'failed': '#6c757d'
        };
        return (
          <span style={{ 
            color: statusColors[status] || '#000',
            fontWeight: '500',
            textTransform: 'capitalize'
          }}>
            {row.status}
          </span>
        );
      }
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <RTButton
          type="link"
          size="sm"
          onClick={() => navigate(`/payments/${row.payment_id}/view`)}
        >
          View Payment
        </RTButton>
      )
    }
  ];

  const preparePaymentTableData = () => {
    return payments.map(payment => ({
      payment_id: payment.payment_id,
      payment_date: formatDateToDDMMYYYY(payment.payment_date),
      amount: payment.amount,
      payment_method: payment.payment_method,
      payment_type: payment.payment_type || 'Payment',
      reference_number: payment.reference_number || '-',
      status: payment.status,
      notes: payment.notes || '-',
      payment: payment
    }));
  };

  return (
    <div style={{ padding: '20px' }}>
      {paymentsLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <RTSpinner message="Loading payments..." />
        </div>
      ) : paymentsError ? (
        <div style={{ textAlign: 'center', color: '#dc3545', padding: '40px' }}>
          <p>{paymentsError}</p>
          <RTButton variant="primary" onClick={() => { setPaymentsLoaded(false); fetchPayments(); }}>
            Retry
          </RTButton>
        </div>
      ) : (
        <>
          {/* Payment Summary */}
          {paymentSummary && (
            <div className="rt-summary-section">
              <div className="rt-summary-table">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ color: '#153C7F', margin: 0 }}>Payment Summary</h4>
                  <RTButton
                    type="link"
                    variant="primary"
                    permissionKey="payments.add_payment"
                    onClick={handleAddPayment}
                  >
                    + Add Payment
                  </RTButton>
                </div>
                <div>
                  <div style={{ display: 'flex', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px', marginRight: '20px', marginBottom: '8px' }}>
                      <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Total Payments:</label>
                      <span>{paymentSummary.totalPayments}</span>
                    </div>
                    <div style={{ flex: '1 1 200px', marginRight: '20px', marginBottom: '8px' }}>
                      <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Completed:</label>
                      <span style={{ color: '#28a745' }}>{paymentSummary.completedPayments}</span>
                    </div>
                    {paymentSummary.cancelledCount > 0 && (
                      <div style={{ flex: '1 1 200px', marginRight: '20px', marginBottom: '8px' }}>
                        <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Cancelled:</label>
                        <span style={{ color: '#dc3545' }}>{paymentSummary.cancelledCount}</span>
                      </div>
                    )}
                    <div style={{ flex: '1 1 200px', marginBottom: '8px' }}>
                      <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Total Amount Paid:</label>
                      <span style={{ fontWeight: '600', color: '#153C7F' }}>₹{paymentSummary.totalAmountPaid?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Table */}
          {payments.length > 0 ? (
            <Table
              title={`Payments (${payments.length} total)`}
              columns={paymentColumns}
              data={preparePaymentTableData()}
              showSearch={true}
              exportable={true}
              showPagination={true}
              pageSize={20}
              noDataText="No payments found"
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>No payments found for this paying guest.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PGPaymentsTab;
